// Feature-gating predicate for the activation journey (feed / dictionary /
// review). A feature unlocks once the user advances past the step that declares
// `unlocks: <feature>`, or — when live `stats` are supplied — once that step's
// own goal is met (e.g. feed → completed 8 lessons), so the gate can't lag
// behind the milestone the locked overlay shows.

import {
  ACTIVATION_COMPLETE_STEP,
  getFeatureUnlockStep,
  isFeatureGoalMet,
  type ActivationLevelStats,
  type ActivationUnlock,
} from "./activationSteps";

export interface ActivationState {
  /** Server-synced `userSettings.activationStep` (1-based; 11 == complete). */
  currentStep: number;
  /** Live stats; when present, also unlocks once the unlock step's goal is met. */
  stats?: ActivationLevelStats;
}

/** Gated features, plus the always-open surfaces callers query. */
export type GatedFeature = ActivationUnlock | "dictionary" | "review";

function isActivationUnlock(feature: GatedFeature): feature is ActivationUnlock {
  return feature === "flashcards" || feature === "feed";
}

export function isFeatureUnlocked(
  feature: GatedFeature,
  state: ActivationState
): boolean {
  // dictionary / review have no unlock step — always open.
  if (!isActivationUnlock(feature)) return true;

  const unlockStep = getFeatureUnlockStep(feature);
  if (!unlockStep) return true;

  if (state.currentStep >= ACTIVATION_COMPLETE_STEP) return true;
  if (state.currentStep > unlockStep.step) return true;

  if (state.stats && isFeatureGoalMet(feature, state.stats)) return true;

  return false;
}
